import React, { useState, useEffect, useMemo, useRef } from "react";
import SidebarHeader from "./SidebarHeader";
import ChannelItem from "./ChannelItem";
import KeywordsSection from "./KeywordsSection";
import SidebarFooter from "./SidebarFooter";
import ChannelSettingsModal from "./ChannelSettingsModal";
import { ChevronDown, ChevronUp, Radio, GripVertical } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import logger from "../../utils/logger";
import { io } from "socket.io-client";

const TeamsSidebar = ({
  isDarkMode,
  toggleTheme,
  setIsDarkMode,
  channels,
  setChannels,
  activeChannels,
  setActiveChannels,
  activeKeywords,
  toggleKeyword,
  keywordCounts,
  channelMessageCounts,
  API_BASE_URL,
  isMobile,
  closeSidebar,
  onDocumentationClick,
  branding
}) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("channels");
  const [keywordSectionHeight, setKeywordSectionHeight] = useState("max-h-96");
  const [isChannelsExpanded, setIsChannelsExpanded] = useState(true);
  const [isKeywordsExpanded, setIsKeywordsExpanded] = useState(true);
  const [draggedChannelId, setDraggedChannelId] = useState(null);
  const [dragOverChannelId, setDragOverChannelId] = useState(null);
  const socketRef = useRef(null);
  const isUpdatingFromSocketRef = useRef(false);

  useEffect(() => {
    const storedActiveChannels = JSON.parse(localStorage.getItem("activeChannels"));
    if (storedActiveChannels) setActiveChannels(storedActiveChannels);
  }, [setActiveChannels]);

  // Initialize WebSocket connection for real-time position updates
  useEffect(() => {
    if (!API_BASE_URL) return;

    // Skip WebSocket connection for cloud services (cs.boondock.cloud)
    // Cloud services may not support WebSocket or may require different authentication
    if (API_BASE_URL.includes('cs.boondock.cloud')) {
      logger.debug('Skipping WebSocket connection for cloud service');
      return;
    }

    // Get socket URL from API_BASE_URL
    // Extract base URL (remove /api if present)
    let baseUrl = API_BASE_URL;
    if (baseUrl.includes('/api')) {
      baseUrl = baseUrl.replace('/api', '');
    }
    // Convert http/https to ws/wss
    const socketUrl = baseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    
    logger.debug('Connecting to WebSocket for channel positions:', socketUrl);
    
    socketRef.current = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socketRef.current.on('connect', () => {
      logger.debug('WebSocket connected for channel positions');
    });

    socketRef.current.on('channel_positions_updated', (data) => {
      // Ignore updates that we just sent ourselves
      if (isUpdatingFromSocketRef.current) {
        isUpdatingFromSocketRef.current = false;
        return;
      }

      logger.debug('Received channel positions update from another tab:', data);
      
      if (data && data.positions) {
        // Update channels with new positions from other tabs with smooth animation
        setChannels((prevChannels) => {
          const updatedChannels = { ...prevChannels };
          data.positions.forEach(({ id, position }) => {
            if (updatedChannels[id]) {
              updatedChannels[id] = {
                ...updatedChannels[id],
                position: position
              };
            }
          });
          return updatedChannels;
        });
      }
    });

    socketRef.current.on('disconnect', () => {
      logger.debug('WebSocket disconnected for channel positions');
    });

    socketRef.current.on('connect_error', (error) => {
      // Only log at debug level to avoid console noise
      logger.debug('WebSocket connection error:', error);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [API_BASE_URL, setChannels]);

  // Dynamic height calculation based on window size
  useEffect(() => {
    const calculateKeywordHeight = () => {
      const windowHeight = window.innerHeight;
      const isSmallScreen = windowHeight < 600;
      const isMediumScreen = windowHeight >= 600 && windowHeight < 800;
      const isLargeScreen = windowHeight >= 800;

      if (isSmallScreen) {
        setKeywordSectionHeight("max-h-48"); // 192px for small screens
      } else if (isMediumScreen) {
        setKeywordSectionHeight("max-h-64"); // 256px for medium screens
      } else {
        setKeywordSectionHeight("max-h-96"); // 384px for large screens
      }
    };

    calculateKeywordHeight();
    window.addEventListener('resize', calculateKeywordHeight);
    
    return () => window.removeEventListener('resize', calculateKeywordHeight);
  }, []);

  const handleToggleChannel = (channelId) => {
    setActiveChannels((prev) => {
      const updatedChannels = { ...prev, [channelId]: !prev[channelId] };
      localStorage.setItem("activeChannels", JSON.stringify(updatedChannels));
      return updatedChannels;
    });
  };

  const handleSettingsClick = (channel) => {
    setSelectedChannel(channel);
    setIsSettingsOpen(true);
  };

  // Convert channels object to sorted array by position
  const sortedChannels = useMemo(() => {
    const channelsArray = Object.entries(channels)
      .map(([channelId, channel]) => {
        // Use channel.id if it exists and is a number, otherwise use the key
        const id = (channel && typeof channel.id === 'number') 
          ? channel.id 
          : (channel && channel.id) 
            ? parseInt(channel.id, 10) 
            : parseInt(channelId, 10);
        
        return {
          id: id,
          ...channel,
          position: (channel && channel.position !== undefined && channel.position !== null) 
            ? parseInt(channel.position, 10) 
            : 0
        };
      })
      .sort((a, b) => {
        const posA = parseInt(a.position, 10) || 0;
        const posB = parseInt(b.position, 10) || 0;
        if (posA !== posB) {
          return posA - posB;
        }
        return (a.id || 0) - (b.id || 0);
      });
    
    // Only log if channels have positions set
    const hasPositions = channelsArray.some(c => c.position !== 0);
    if (hasPositions) {
      logger.debug('Sorted channels by position:', channelsArray.map(c => ({ id: c.id, name: c.name, position: c.position })));
    }
    return channelsArray;
  }, [channels]);

  // Handle drag start
  const handleDragStart = (e, channelId) => {
    setDraggedChannelId(channelId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', channelId);
  };

  // Handle drag over
  const handleDragOver = (e, channelId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverChannelId(channelId);
  };

  // Handle drag leave
  const handleDragLeave = () => {
    setDragOverChannelId(null);
  };

  // Handle drop
  const handleDrop = async (e, targetChannelId) => {
    e.preventDefault();
    setDragOverChannelId(null);

    if (!draggedChannelId || draggedChannelId === targetChannelId) {
      setDraggedChannelId(null);
      return;
    }

    try {
      // Get current positions
      const draggedIndex = sortedChannels.findIndex(c => c.id === draggedChannelId);
      const targetIndex = sortedChannels.findIndex(c => c.id === targetChannelId);

      if (draggedIndex === -1 || targetIndex === -1) {
        setDraggedChannelId(null);
        return;
      }

      // Create new positions array
      const newPositions = sortedChannels.map((channel, index) => {
        let newPosition = index;
        
        if (draggedIndex < targetIndex) {
          // Moving down
          if (index > draggedIndex && index <= targetIndex) {
            newPosition = index - 1;
          } else if (index === draggedIndex) {
            newPosition = targetIndex;
          }
        } else {
          // Moving up
          if (index >= targetIndex && index < draggedIndex) {
            newPosition = index + 1;
          } else if (index === draggedIndex) {
            newPosition = targetIndex;
          }
        }

        return {
          id: typeof channel.id === 'number' ? channel.id : parseInt(channel.id, 10),
          position: newPosition
        };
      });

      // Optimistically update UI first for immediate feedback
      const updatedChannelsMap = { ...channels };
      newPositions.forEach(({ id, position }) => {
        if (updatedChannelsMap[id]) {
          updatedChannelsMap[id] = {
            ...updatedChannelsMap[id],
            position: position
          };
        }
      });
      setChannels(updatedChannelsMap);

      // Update positions via API
      logger.debug('Updating positions:', newPositions);
      isUpdatingFromSocketRef.current = true; // Mark that we're sending an update
      const response = await axios.put(`${API_BASE_URL}/channels/positions`, {
        positions: newPositions
      });

      if (response.status === 200) {
        // Don't refresh immediately - trust the optimistic update
        // The positions are already saved to the database and will be loaded on next periodic refresh
        logger.debug('Positions saved successfully. Channels will refresh on next update cycle.');
        logger.debug('Updated channels map with positions:', 
          Object.values(updatedChannelsMap).map(c => ({ id: c.id, name: c.name, position: c.position }))
        );
        // Success notification removed - only show errors
        
        // Note: Backend will broadcast the update via WebSocket, so we don't need to emit here
        // This prevents duplicate updates
      }
    } catch (error) {
      logger.error('Error updating channel positions:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update channel positions';
      logger.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: errorMessage
      });
      toast.error(`Failed to update channel positions: ${errorMessage}`);
    } finally {
      setDraggedChannelId(null);
      isUpdatingFromSocketRef.current = false;
    }
  };

  const handleSave = async (channelId, updatedChannel) => {
    setIsSaving(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/channel/${channelId}`, updatedChannel);
      if (response.data) {
        setChannels((prevChannels) => ({
          ...prevChannels,
          [channelId]: { ...prevChannels[channelId], ...updatedChannel },
        }));
      }
      setIsSettingsOpen(false);
    } catch (error) {
      logger.error("Error updating channel:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeywordClick = (keyword) => toggleKeyword(keyword);

  return (
    <div
      className={`flex flex-col h-full max-h-screen transition-colors duration-300 ${
        isDarkMode ? "bg-gray-900 border-gray-700" : "bg-gray-50 border-gray-200"
      } ${isMobile ? 'w-full' : 'w-full'}`}
    >
      <SidebarHeader 
        isDarkMode={isDarkMode} 
        toggleTheme={toggleTheme}
        isMobile={isMobile}
        closeSidebar={closeSidebar}
        edgeServerEndpoint={API_BASE_URL}
        organizationName={branding?.organizationName}
      />

      {/* Tab Content */}
      <div className={`flex-grow flex flex-col overflow-hidden ${isDarkMode ? "dark-mode-scrollbar" : ""}`}>
        {/* Channels Section - With title */}
        <div className="flex flex-col flex-1 min-h-0">
          {/* Header */}
          <div
            className={`flex items-center px-4 py-3 border-b ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <Radio
                className={`w-5 h-5 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}
              />
              <h2
                className={`text-lg font-semibold ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                {branding?.channels_label || 'Channels'}
              </h2>
            </div>
          </div>
          <div className={`flex-1 overflow-y-auto px-4 py-2 pb-6 ${isDarkMode ? "dark-mode-scrollbar" : ""}`}>
            <div className="space-y-2">
              {sortedChannels.map((channel, index) => {
                const channelId = channel.id;
                const isDragging = draggedChannelId === channelId;
                const isDragOver = dragOverChannelId === channelId;
                const position = parseInt(channel.position, 10) || index;
                
                return (
                  <div
                    key={`channel-${channelId}-pos-${position}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, channelId)}
                    onDragOver={(e) => handleDragOver(e, channelId)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, channelId)}
                    className={`relative transition-all duration-500 ease-in-out ${
                      isDragging ? 'opacity-50 z-50' : 'opacity-100'
                    } ${
                      isDragOver ? 'border-t-2 border-blue-500' : ''
                    }`}
                    style={{
                      transform: isDragging ? 'scale(0.98)' : 'scale(1)',
                      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    <div className="flex items-center">
                      <div
                        className={`cursor-move p-1 mr-1 flex items-center gap-1.5 ${
                          isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                        }`}
                        draggable={false}
                      >
                        <GripVertical size={16} />
                        <span className={`text-xs font-semibold min-w-[1.5rem] text-center ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {position + 1}
                        </span>
                      </div>
                      <div className="flex-1 transition-all duration-500 ease-in-out">
                        <ChannelItem
                          channel={channel}
                          isActive={activeChannels[channelId]}
                          channelMessageCounts={channelMessageCounts}
                          isDarkMode={isDarkMode}
                          handleToggleChannel={() => handleToggleChannel(channelId)}
                          handleSettingsClick={handleSettingsClick}
                          edgeServerEndpoint={API_BASE_URL}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Keywords Section - Collapsible */}
        <div className={`flex flex-col ${isKeywordsExpanded ? 'flex-1 min-h-0' : ''}`}>
          <button
            onClick={() => setIsKeywordsExpanded(!isKeywordsExpanded)}
            className={`w-full flex items-center justify-between py-2 px-2 rounded-lg transition-colors flex-shrink-0 ${
              isDarkMode
                ? 'hover:bg-gray-800 text-gray-300'
                : 'hover:bg-gray-100 text-gray-700'
            }`}
          >
            <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Keyword Tracker
            </h3>
            {isKeywordsExpanded ? (
              <ChevronUp className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            ) : (
              <ChevronDown className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            )}
          </button>
          {isKeywordsExpanded && (
            <div className={`flex-1 overflow-y-auto mt-2 min-h-0 ${isDarkMode ? "dark-mode-scrollbar" : ""}`}>
              <KeywordsSection
                keywordCounts={keywordCounts}
                activeKeywords={activeKeywords}
                handleKeywordClick={handleKeywordClick}
                isDarkMode={isDarkMode}
                maxHeightClass="h-full"
              />
            </div>
          )}
        </div>
      </div>

      <SidebarFooter activeChannels={activeChannels} isDarkMode={isDarkMode} />
      <ChannelSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        channel={selectedChannel}
        onSave={handleSave}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default TeamsSidebar;